/**
 * Viewer-role derivation and action-gating helpers.
 *
 * Per spec §9.1 "Actions (contextual to viewer + state)" — every action in
 * the Hub is conditioned on the viewer's role on the engagement and on the
 * engagement state. This module owns that derivation in one place so render
 * sites stay declarative.
 *
 * Notes:
 * - "Engagement creator" is treated as audit-only metadata, not an
 *   operational role. A creator who is also a project owner is shown as
 *   "Work approver", not "Engagement creator".
 * - Player-coach pattern (a single user is both work approver AND
 *   governance approver) is supported: `deriveUserRoleLabels` returns ALL
 *   roles a user holds, joined by the caller. `deriveViewerRole` returns
 *   the most-operational single role for action gating.
 * - State-aware gates take `(engagement, viewerRole, viewerUserId)`. The
 *   `viewerUserId` parameter distinguishes "customer creator" (the user who
 *   initiated the request) from other customer-side users — the spec §9.1
 *   action table reserves Withdraw/Accept/Reject specifically for the
 *   creator, even when other work approvers can chat.
 */
import { EngagementParticipantRole } from '@/__generated__/graphql';

import type { EngagementView } from '../../live/viewModel';
import type {
  DirectMessageChannelType,
  Engagement,
  EngagementAmendment,
  Project,
} from '../../types';

/**
 * Mock-side OnFrontiers Fulfillment user ids. In production this is a
 * permission/group membership check, not an id allowlist.
 *
 * TODO: confirm with eng — canonical OF Fulfillment role / group identifier.
 */
export const FULFILLMENT_USER_IDS = new Set<string>(['usr_james_b']);

export type ViewerRole =
  | 'expert'
  | 'work_approver'
  | 'governance_approver'
  | 'project_member'
  | 'of_fulfillment'
  | 'unknown';

const ROLE_LABEL = {
  expert: 'Expert',
  work_approver: 'Work approver',
  governance_approver: 'Governance approver',
  project_member: 'Project member',
  of_fulfillment: 'OnFrontiers',
  unknown: 'Viewer',
  // Used by viewerChipLabel only — not part of the operational ViewerRole
  // enum, which treats "creator" as audit-only metadata.
  customer_creator: 'Customer creator',
} as const satisfies Record<ViewerRole | 'customer_creator', string>;

/**
 * The role-bearing facts a viewer's role is derived from, independent of where
 * they came from. ENG-5448 (FE2a) added this seam so live GraphQL data and the
 * prototype scene feed ONE derivation rather than two — the README's
 * "single role derivation" invariant, restated for two input shapes.
 */
export interface RoleSubject {
  expertId: string | null;
  governanceApproverIds: string[];
  workApproverIds: string[];
  projectMemberIds: string[];
}

/**
 * The viewer's identity as the session knows it. `isOfStaff` replaces the
 * mock-side `FULFILLMENT_USER_IDS` allowlist on live surfaces — production
 * asks the session, not an id set.
 */
export interface SessionViewer {
  userId: string;
  isOfStaff: boolean;
}

/**
 * Single most-operational role for the viewer. Order:
 * expert > work_approver > governance_approver > project_member > of_fulfillment.
 *
 * Operational ordering reflects who is most likely to TAKE action in this
 * surface; for the Active state Hub, work approver outranks governance
 * approver (governance is past-tense at this point — they approved before
 * activation; work approver is currently confirming hours).
 */
export function deriveViewerRoleFromSubject(viewer: SessionViewer, s: RoleSubject): ViewerRole {
  if (s.expertId && viewer.userId === s.expertId) return 'expert';
  if (s.workApproverIds.includes(viewer.userId)) return 'work_approver';
  if (s.governanceApproverIds.includes(viewer.userId)) return 'governance_approver';
  if (s.projectMemberIds.includes(viewer.userId)) return 'project_member';
  if (viewer.isOfStaff) return 'of_fulfillment';
  return 'unknown';
}

/**
 * Live entry shape. `participants` is null when the viewer isn't entitled to
 * the roster (the expert side never sees it) — an empty roster is the correct
 * reading there, because the expert's own role resolves from `expertId` and
 * they hold no customer-side role by definition.
 */
export function roleSubjectFromLive(view: EngagementView): RoleSubject {
  const participants = view.participants ?? [];
  return {
    expertId: view.expertId,
    governanceApproverIds: participants
      .filter((p) => p.role === 'governance_approver')
      .map((p) => p.userId),
    workApproverIds: participants.filter((p) => p.role === 'work_approver').map((p) => p.userId),
    projectMemberIds: view.project?.memberUserIds ?? [],
  };
}

/** Prototype-scene entry shape. Kept so mock surfaces and stories don't change. */
export function roleSubjectFromMock(engagement: Engagement, project: Project): RoleSubject {
  return {
    expertId: engagement.expert_id,
    governanceApproverIds: engagement.governance_approver_id
      ? [engagement.governance_approver_id]
      : [],
    workApproverIds: engagement.work_approver_user_ids,
    projectMemberIds: project.project_member_user_ids,
  };
}

/**
 * Mock-typed entry point — a thin wrapper so the derivation itself has exactly
 * one implementation. `of_fulfillment` still resolves from the mock allowlist
 * here; live callers pass real session staff-ness instead.
 */
export function deriveViewerRole(
  viewerUserId: string,
  engagement: Engagement,
  project: Project
): ViewerRole {
  return deriveViewerRoleFromSubject(
    { userId: viewerUserId, isOfStaff: FULFILLMENT_USER_IDS.has(viewerUserId) },
    roleSubjectFromMock(engagement, project)
  );
}

/**
 * All roles a user holds on this engagement, ordered most-operational first.
 * Used by the Parties block, where a user may legitimately hold multiple
 * roles (player-coach, OnFrontiers Fulfillment + project member, etc.).
 */
export function deriveUserRoleLabels(
  userId: string,
  engagement: Engagement,
  project: Project
): string[] {
  const labels: string[] = [];
  if (engagement.expert_id === userId) labels.push(ROLE_LABEL.expert);
  if (engagement.work_approver_user_ids.includes(userId)) labels.push(ROLE_LABEL.work_approver);
  if (engagement.governance_approver_id === userId) labels.push(ROLE_LABEL.governance_approver);
  if (project.project_member_user_ids.includes(userId)) labels.push(ROLE_LABEL.project_member);
  if (FULFILLMENT_USER_IDS.has(userId)) labels.push(ROLE_LABEL.of_fulfillment);
  return labels;
}

export function viewerRoleLabel(role: ViewerRole): string {
  return ROLE_LABEL[role];
}

/**
 * Display-only label for the Hub's "Viewing as" chip. **Independent of
 * `deriveViewerRole`** — the gating-role resolution uses operational
 * priority (work approver outranks governance approver during ACTIVE
 * because work approvers are currently confirming hours), but the chip
 * label uses authority priority so the most load-bearing roles surface
 * first. This is what makes the §6.3 role-conflation case readable at a
 * glance.
 *
 * Priority (first match wins):
 *   1. Expert
 *   2. Customer creator + Governance approver  ← role-conflation §6.3
 *   3. Customer creator                        ← creator-only
 *   4. Governance approver                     ← gov-only (not creator)
 *   5. Work approver
 *   6. Project member
 *   7. OnFrontiers (fulfillment)
 *   8. Viewer (fallback)
 *
 * The composed creator+governance label (#2) is the load-bearing case for
 * §9.1 banner variant (b) — the chip needs to make the dual role visible
 * because that pairing is exactly what drives the auto-approval collapse.
 */
export function viewerChipLabel(
  viewerUserId: string,
  engagement: Engagement,
  project: Project
): string {
  if (engagement.expert_id && viewerUserId === engagement.expert_id) {
    return ROLE_LABEL.expert;
  }
  const isCreator = engagement.created_by_user_id === viewerUserId;
  const isGovernance = engagement.governance_approver_id === viewerUserId;
  if (isCreator && isGovernance) {
    return `${ROLE_LABEL.customer_creator} + ${ROLE_LABEL.governance_approver}`;
  }
  if (isCreator) return ROLE_LABEL.customer_creator;
  if (isGovernance) return ROLE_LABEL.governance_approver;
  if (engagement.work_approver_user_ids.includes(viewerUserId)) {
    return ROLE_LABEL.work_approver;
  }
  if (project.project_member_user_ids.includes(viewerUserId)) {
    return ROLE_LABEL.project_member;
  }
  if (FULFILLMENT_USER_IDS.has(viewerUserId)) return ROLE_LABEL.of_fulfillment;
  return ROLE_LABEL.unknown;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function isCreator(e: Engagement, viewerUserId: string): boolean {
  return e.created_by_user_id === viewerUserId;
}

function awaitingCustomer(e: Engagement): boolean {
  return e.status === 'pending_confirmation' && e.awaiting === 'customer';
}

function awaitingGovernance(e: Engagement): boolean {
  return e.status === 'pending_confirmation' && e.awaiting === 'governance';
}

/**
 * Spec §8.6 outcomes B/C: governance has bounced feedback (adjustment or
 * rejection) back to the customer creator. The standard awaiting-customer
 * accept/counter/reject affordances are replaced with mediation actions
 * (forward / counter / withdraw) when this is true.
 */
export function hasGovernanceFeedback(e: Engagement): boolean {
  return (
    e.status === 'pending_confirmation' &&
    e.awaiting === 'customer' &&
    e.governance_feedback !== null &&
    e.governance_feedback !== undefined
  );
}

export function governanceFeedbackKind(e: Engagement): 'adjustment' | 'rejection' | null {
  if (e.status !== 'pending_confirmation') return null;
  if (e.awaiting !== 'customer') return null;
  return e.governance_feedback?.kind ?? null;
}

// ─── Rate visibility (spec §6.3 / §11.2 #9) ─────────────────────────────────

/**
 * Per spec §6.3: `expert_rate_amount_cents` is OF-internal and never displayed
 * to the customer; `customer_rate_loaded_cents` is the only rate the customer
 * sees. Expert sees their own rate. OF Fulfillment can see both — for the
 * Hub display we default OF to the customer-loaded view since they're an
 * observer of the customer-side narrative.
 */
export function viewerSeesExpertRate(role: ViewerRole): boolean {
  return role === 'expert';
}

// ─── Pre-offer gates (PENDING_OFFER) ────────────────────────────────────────

/** Spec §9.1: "Make Offer" — expert only, only in PENDING_OFFER. */
export function canMakeOffer(e: Engagement, role: ViewerRole): boolean {
  return canMakeOfferFor(e.status, role);
}

/** String-keyed twin for live data (ENG-5448). */
export function canMakeOfferFor(status: string, role: ViewerRole): boolean {
  return status === 'pending_offer' && role === 'expert';
}

/**
 * Live counterpart of `canAcceptOffer` (ENG-5448, narrowed in ENG-5605).
 *
 * Creator-only, matching the mock gate and spec §9.1: the customer who opened
 * the request is the one who accepts it, even though other work approvers can
 * chat. FE2a widened this to any customer-side viewer because `createdByUserId`
 * was not on the read model (gap G4) and leaned on server authorization for the
 * distinction. ENG-5604 landed the field, so the client no longer offers a
 * button whose only outcome would be a server rejection.
 *
 * Suppressed once governance has spoken, for the same reason as the mock twin:
 * `KindGovernanceReturn` lands on exactly this status/awaiting pair while
 * writing `governance_feedback` (`api/engagement/service/engagement.go`), so
 * without this the creator would get "Governance rejected this engagement."
 * above a live Accept button. Past that point the creator's choices are
 * forward / counter / withdraw, not re-accept.
 */
export function canAcceptOfferLive(
  status: string,
  awaiting: string | null,
  createdByUserId: string,
  viewerUserId: string,
  hasGovernanceFeedback: boolean
): boolean {
  if (status !== 'pending_confirmation' || awaiting !== 'customer') return false;
  if (hasGovernanceFeedback) return false;
  return createdByUserId === viewerUserId;
}

/** Spec §9.1: "Decline" — expert in PENDING_OFFER. */
export function canDeclineOffer(e: Engagement, role: ViewerRole): boolean {
  return e.status === 'pending_offer' && role === 'expert';
}

/** String-keyed twin for live data (ENG-5605, gap G1). */
export function canDeclineOfferLive(status: string, role: ViewerRole): boolean {
  return status === 'pending_offer' && role === 'expert';
}

/**
 * Live twin of `canWithdrawRequest` (ENG-5605, gap G1) — the customer creator,
 * before activation. Available while the offer is outstanding, and while the
 * customer still owes a response; once they have accepted, the way out is
 * governance or an amendment, not withdraw.
 */
export function canWithdrawRequestLive(
  status: string,
  awaiting: string | null,
  createdByUserId: string,
  viewerUserId: string
): boolean {
  if (createdByUserId !== viewerUserId) return false;
  if (status === 'pending_offer') return true;
  return status === 'pending_confirmation' && awaiting === 'customer';
}

/**
 * Spec §9.1: "Withdraw Request" — customer creator before activation.
 * Available in pending_offer, and in pending_confirmation when the customer
 * still owes a response (awaiting includes 'customer'). Once the customer
 * has accepted, the path forward is governance or amendment, not withdraw.
 *
 * Withdraw remains available when governance has bounced feedback back —
 * per spec §8.6 it's one of the customer creator's mediation choices.
 */
export function canWithdrawRequest(
  e: Engagement,
  _role: ViewerRole,
  viewerUserId: string
): boolean {
  if (!isCreator(e, viewerUserId)) return false;
  if (e.status === 'pending_offer') return true;
  if (e.status === 'pending_confirmation' && awaitingCustomer(e)) return true;
  return false;
}

// ─── Customer-side acceptance gates (PENDING_CONFIRMATION) ──────────────────

/**
 * Spec §9.1: "Accept Offer" — customer creator while customer accept is
 * outstanding (`awaiting === 'customer'`). Strictly serial: governance is
 * never notified before customer accept.
 *
 * Suppressed when `governance_feedback` is present — the offer has already
 * moved past initial customer accept and the creator's choices are now
 * forward/counter/withdraw, not re-accept.
 */
export function canAcceptOffer(e: Engagement, _role: ViewerRole, viewerUserId: string): boolean {
  return isCreator(e, viewerUserId) && awaitingCustomer(e) && !hasGovernanceFeedback(e);
}

/** Spec §9.1: "Reject" — same gate as Accept. */
export function canRejectOffer(e: Engagement, role: ViewerRole, viewerUserId: string): boolean {
  return canAcceptOffer(e, role, viewerUserId);
}

/**
 * Spec §9.1: "Counter (chat-and-update)" — eligibility mirrors Accept for
 * the standard awaiting-customer path. Available *also* in the governance-
 * feedback state (both adjustment and rejection outcomes), where it's one
 * of the creator's mediation choices.
 */
export function canCounterOffer(e: Engagement, _role: ViewerRole, viewerUserId: string): boolean {
  if (!isCreator(e, viewerUserId)) return false;
  return awaitingCustomer(e);
}

// ─── Creator mediation gates after governance feedback (§8.6 B/C) ───────────

/**
 * Spec §8.6 outcome B: customer creator forwards the governance-proposed
 * adjustment to the expert for re-confirmation. Engagement returns to
 * PENDING_OFFER with the adjusted NTE.
 */
export function canForwardAdjustment(
  e: Engagement,
  _role: ViewerRole,
  viewerUserId: string
): boolean {
  return isCreator(e, viewerUserId) && governanceFeedbackKind(e) === 'adjustment';
}

// ─── Governance gates (PENDING_CONFIRMATION) ────────────────────────────────

/** Spec §9.1 / §8.6: governance approver, when awaiting includes 'governance'. */
export function canApproveEngagement(e: Engagement, role: ViewerRole): boolean {
  return role === 'governance_approver' && awaitingGovernance(e);
}

/** Spec §8.6: "Approve with adjustment" — same gate as Approve. */
export function canApproveWithAdjustment(e: Engagement, role: ViewerRole): boolean {
  return canApproveEngagement(e, role);
}

/** Spec §8.6: "Reject with note" — same gate as Approve. */
export function canRejectEngagement(e: Engagement, role: ViewerRole): boolean {
  return canApproveEngagement(e, role);
}

/**
 * String-keyed twin of `canApproveEngagement` for live data (ENG-5641).
 *
 * Drives the Hub's governance banner: its "Your approval is needed." title and
 * the three decisions beneath it (ENG-5752), which are the same
 * `GovernanceDecisionControls` the Approvals row renders.
 *
 * Reads the ROSTER, not `deriveViewerRole`'s collapsed single role. A
 * player-coach holds both work_approver and governance_approver, and the
 * collapse returns the FIRST match, which is work_approver. Gating on that told
 * the one person who has to decide that they were waiting on themselves, while
 * the api's `ListViewerGovernanceQueue` joined on their governance row and
 * listed the engagement in their inbox. Same question, two answers.
 *
 * The player-coach is NOT §6.3 conflation, and conflating the two sends you
 * looking for a state that cannot exist. §6.3 is `conflated =
 * IsGovernanceApprover(created_by_user_id)` (`engagement.go:1057`), which keys
 * on the CREATOR; when it holds, `KindCustomerAccept` collapses straight to
 * active and `awaiting` never becomes 'governance'. So a conflated engagement
 * can never reach this gate at all. The reachable case this exists for is a
 * dual-role approver who did not create the engagement.
 *
 * The precedence order in `deriveViewerRoleFromSubject` is deliberately left
 * alone: work_approver outranking governance_approver is correct for the Active
 * state, where governance is past tense. It is only wrong as an authorization
 * input, which is what this is.
 *
 * `role` is the generated enum rather than `string`: a typo'd literal is the
 * one mistake that would disable this gate silently, with every existing test
 * still green, and the enum is what the view model already supplies
 * (`viewModel.ts:243`).
 *
 * `status` and `awaiting` stay strings — they are open strings on the wire and
 * the view model carries them as such, so narrowing them here would only move
 * the assertion, not add one.
 */
export function canApproveEngagementLive(
  status: string,
  awaiting: string | null,
  participants: { userId: string; role: EngagementParticipantRole }[] | null,
  viewerUserId: string
): boolean {
  if (status !== 'pending_confirmation' || awaiting !== 'governance') return false;
  return (participants ?? []).some(
    (p) => p.role === EngagementParticipantRole.GovernanceApprover && p.userId === viewerUserId
  );
}

// ─── Active-state gates (preserved from Session 2; tightened for terminals) ──

/**
 * Submit Hours: expert only. Allowed in `active` and during the closing
 * admin window (for hours performed before closure date). Allowed during
 * the mid-flight cancellation reconciliation window. Disabled in
 * pre-offer / pending_confirmation / closed.
 */
export function canSubmitHours(e: Engagement, role: ViewerRole): boolean {
  if (role !== 'expert') return false;
  if (e.status === 'active' || e.status === 'closing') return true;
  if (
    e.status === 'canceled' &&
    e.cancellation_window_ends_at !== null &&
    e.cancellation_window_ends_at !== undefined
  )
    return true;
  return false;
}

/**
 * Confirm full / Confirm partial / Escalate to OF: work approvers.
 * Allowed during `active`, `closing`, and the mid-flight cancellation
 * reconciliation window. Disabled in pre-offer / closed.
 */
export function canConfirmHours(e: Engagement, role: ViewerRole): boolean {
  if (role !== 'work_approver') return false;
  if (e.status === 'active' || e.status === 'closing') return true;
  if (
    e.status === 'canceled' &&
    e.cancellation_window_ends_at !== null &&
    e.cancellation_window_ends_at !== undefined
  )
    return true;
  return false;
}

/**
 * §8.10 step 6 — OF Fulfillment records the dispute-resolution outcome.
 * Gated to `of_fulfillment` while the engagement can still resolve hours
 * (active, closing, or a mid-flight cancellation reconciliation window). The
 * row-level check (status `escalated`, `resolution == null`) lives at the
 * render site, matching the `canConfirmHours` + `showActions` split.
 */
export function canResolveDispute(e: Engagement, role: ViewerRole): boolean {
  if (role !== 'of_fulfillment') return false;
  if (e.status === 'active' || e.status === 'closing') return true;
  if (
    e.status === 'canceled' &&
    e.cancellation_window_ends_at !== null &&
    e.cancellation_window_ends_at !== undefined
  )
    return true;
  return false;
}

/**
 * §6.4 / §8.10 — the expert may withdraw their own submission before it
 * resolves (the `withdrawn` outcome). `deriveViewerRole` only returns `expert`
 * for the engagement's expert, so the role check also asserts ownership. The
 * row-level check (status `submitted`/`escalated`, `resolution == null`) lives
 * at the render site.
 */
export function canWithdrawSubmission(e: Engagement, role: ViewerRole): boolean {
  if (role !== 'expert') return false;
  if (e.status === 'active' || e.status === 'closing') return true;
  if (
    e.status === 'canceled' &&
    e.cancellation_window_ends_at !== null &&
    e.cancellation_window_ends_at !== undefined
  )
    return true;
  return false;
}

/**
 * Propose Amendment (§8.7 Flow G): either party on an `active` engagement —
 * the expert, a customer-side work approver, or the customer creator.
 * Closing/closed/canceled states intentionally disable amendments — the
 * path forward is closure or a new engagement. The render site additionally
 * gates on `hasOpenAmendment` (one proposal at a time).
 */
export function canProposeAmendment(
  e: Engagement,
  role: ViewerRole,
  viewerUserId: string
): boolean {
  if (e.status !== 'active') return false;
  return role === 'expert' || role === 'work_approver' || isCreator(e, viewerUserId);
}

// ─── Amendment lifecycle gates (§8.7 Flow G / §6.6) ─────────────────────────

/**
 * An amendment is "open" while `proposed` or `pending_governance` — it blocks
 * a new proposal and drives the Hub review surface. Accepted/rejected are
 * terminal.
 */
export function hasOpenAmendment(amendments: { status: string }[]): boolean {
  return amendments.some((a) => a.status === 'proposed' || a.status === 'pending_governance');
}

/** The viewer is the party who proposed this amendment. */
export function isAmendmentProposer(a: EngagementAmendment, viewerUserId: string): boolean {
  return a.proposed_by_user_id === viewerUserId;
}

/** Whether the amendment was proposed by the expert (vs a customer-side party). */
export function amendmentProposedByExpert(a: EngagementAmendment, e: Engagement): boolean {
  return e.expert_id !== null && a.proposed_by_user_id === e.expert_id;
}

/**
 * §8.7 — the counterparty (the party who did NOT propose) confirms a
 * `proposed` amendment. Expert-proposed → a customer-side work approver or the
 * creator confirms; customer-proposed → the expert confirms. The proposer
 * never confirms their own proposal.
 */
export function canConfirmAmendment(
  e: Engagement,
  a: EngagementAmendment,
  role: ViewerRole,
  viewerUserId: string
): boolean {
  if (e.status !== 'active' || a.status !== 'proposed') return false;
  if (isAmendmentProposer(a, viewerUserId)) return false;
  if (amendmentProposedByExpert(a, e)) {
    return role === 'work_approver' || isCreator(e, viewerUserId);
  }
  return role === 'expert';
}

/** §8.7 — the proposer may withdraw their own proposal while it stays open. */
export function canWithdrawAmendment(
  e: Engagement,
  a: EngagementAmendment,
  viewerUserId: string
): boolean {
  if (e.status !== 'active') return false;
  if (a.status !== 'proposed' && a.status !== 'pending_governance') return false;
  return isAmendmentProposer(a, viewerUserId);
}

/**
 * §6.6/§8.7 — the governance approver acts on a threshold-crossing amendment.
 * Subject to the §6.3 role-conflation collapse: when the proposer is also the
 * governance approver the step auto-resolves (see AmendmentReview). Funding-
 * mode switch + §9.13 billing are Phase 3b — not gated here.
 */
export function canApproveAmendmentGovernance(
  e: Engagement,
  a: EngagementAmendment,
  role: ViewerRole
): boolean {
  return (
    e.status === 'active' && a.status === 'pending_governance' && role === 'governance_approver'
  );
}

/**
 * Close Engagement: expert + work approvers, only on `active` engagements.
 * Already-closing engagements hide the affordance (per S4); terminal
 * states have nothing to close.
 */
export function canCloseEngagement(e: Engagement, role: ViewerRole): boolean {
  if (e.status !== 'active') return false;
  return role === 'expert' || role === 'work_approver';
}

// ─── Messaging ──────────────────────────────────────────────────────────────

/**
 * Spec §9.8: Customer-side users (governance + work approvers + project
 * members), the expert, and OF Fulfillment can chat. Message composer is hidden
 * entirely on terminal CLOSED engagements (read-only) and on CANCELED
 * engagements; pending_offer/pending_confirmation/active/closing all allow
 * messaging — the dialogue is load-bearing during refinement and approval.
 *
 * ENG-5439 dropped the `of_invited` gate: OF staff read every channel by role and
 * are recorded by the act of sending, so an invite is no longer what lets them
 * speak. The `of_invited` stub on the prototype engagement record is now unread
 * here — it survives in types.ts and the mock scenes, which are Storybook-only.
 */
export function canSendMessage(e: Engagement, role: ViewerRole): boolean {
  return canSendMessageFor(e.status, role);
}

/**
 * String-keyed twin for live data (ENG-5448).
 *
 * OF Fulfillment may always send (ENG-5439). The invite-only default this used
 * to carry was built on a membership model that no longer exists: staff now read
 * every channel by role, and it is the SEND that records them, so requiring an
 * invite before they can speak would have made the reveal unreachable. The
 * server wall is the authority either way — this only decides whether the
 * composer is offered.
 */
export function canSendMessageFor(status: string, role: ViewerRole): boolean {
  if (status === 'closed' || status === 'canceled') return false;
  if (role === 'unknown') return false;
  return true;
}

/**
 * §9.8 sub-channel visibility matrix. The trilateral chat is always present;
 * the OF bilateral channels split strictly by side:
 *   - of_client: customer-side users + OF Fulfillment (expert NOT visible)
 *   - of_expert: expert + OF Fulfillment (customer-side NOT visible)
 * A viewer sees only the sub-channels they have visibility into — cross-side
 * channels are never rendered (a §6.3-grade invariant, not a CSS hide).
 */
export function visibleDirectChannelTypes(role: ViewerRole): DirectMessageChannelType[] {
  if (role === 'of_fulfillment') return ['of_client', 'of_expert'];
  if (role === 'expert') return ['of_expert'];
  if (role === 'work_approver' || role === 'governance_approver' || role === 'project_member') {
    return ['of_client'];
  }
  return [];
}

// ─── Live hours gates (ENG-5449 / FE3) ──────────────────────────────────────

/**
 * String-keyed twin of `canSubmitHours` for live data.
 *
 * Deliberately narrower than the mock gate, which also allows the 7-day
 * reconciliation window on a `canceled` engagement. The api mirrors that split:
 * `checkEngagementHoursGate` accepts the canceled window, but
 * `engagementAcceptsHours` — the predicate that decides whether the render-only
 * `not_yet_submitted` row is returned at all — is `active || closing`. Since
 * that synthetic row IS the form's placement, a wider gate here would only
 * describe a state the panel can never reach.
 */
export function canSubmitHoursFor(status: string, role: ViewerRole): boolean {
  return role === 'expert' && (status === 'active' || status === 'closing');
}

/**
 * String-keyed twin of `canConfirmHours`. Work approver, while the engagement
 * can still resolve hours.
 */
export function canConfirmHoursFor(status: string, role: ViewerRole): boolean {
  return role === 'work_approver' && (status === 'active' || status === 'closing');
}

/**
 * Dispute resolution — **work approver, not `of_fulfillment`**.
 *
 * The mock twin `canResolveDispute` gates `of_fulfillment` on the strength of
 * §8.10's "OF Fulfillment records the outcome". The wire disagrees:
 * `resolveEngagementDispute` rejects any actor who is not a work approver
 * (`engagement.hours_actor_not_work_approver`), and the OF-staff resolver is
 * deferred. Gating OF here would offer a button whose only outcome is a server
 * rejection, and would withhold it from the person who can actually act.
 */
export function canResolveDisputeFor(status: string, role: ViewerRole): boolean {
  return role === 'work_approver' && (status === 'active' || status === 'closing');
}

// ─── Live amendment gates (ENG-5449 / FE3) ──────────────────────────────────

/**
 * Which side of an amendment a viewer is on, mirroring the api's
 * `amendmentPartyOf` (`engagement/service/amendment.go:240`) exactly: the
 * expert, a **snapshotted work approver**, or nobody.
 *
 * The narrowness is the point. Governance approvers, project members and OF
 * staff are NOT parties to an amendment — the api answers them
 * `engagement.amendment_not_participant`, whose own copy states the rule:
 * "Only the expert or a work approver can amend this engagement." A gate of
 * `role !== 'expert'` would hand all three an Accept button whose only possible
 * outcome is that rejection.
 *
 * `roleSubjectFromLive` derives `work_approver` from `view.participants` — the
 * same frozen snapshot `loadParticipants` reads server-side — so this mirrors
 * the wire rather than paralleling it. Note the snapshot is frozen at request
 * time, so a project owner added later is deliberately not a party.
 */
type AmendmentSide = 'expert' | 'customer';

function amendmentSideOf(role: ViewerRole): AmendmentSide | null {
  if (role === 'expert') return 'expert';
  if (role === 'work_approver') return 'customer';
  return null;
}

/**
 * Propose an amendment: either party, on an `active` engagement.
 *
 * Deliberately **not** widened to the engagement creator, unlike the mock twin.
 * `ProposeAmendment` gates on `amendmentPartyOf(actor) != partyNone`, and a
 * creator who is not in the snapshotted work-approver set is `partyNone`. So the
 * creator clause is either redundant (the usual case, where the creator is a
 * project owner and therefore a work approver) or wrong — never useful.
 *
 * The render site also gates on `hasOpenAmendment`: the api allows one live
 * amendment at a time (`engagement.amendment_already_open`).
 */
export function canProposeAmendmentFor(status: string, role: ViewerRole): boolean {
  return status === 'active' && amendmentSideOf(role) !== null;
}

/**
 * Confirm or counter a live proposal: the **opposite side** from the proposer,
 * on an `active` engagement.
 *
 * Compares sides, not user ids — which is what the api does
 * (`actorParty == amendmentPartyOf(proposer)` ⇒ `amendment_not_counterparty`).
 * A user-id comparison would let a *second* work approver accept their
 * colleague's proposal, which the server rejects. Comparing sides also subsumes
 * the self-check: the proposer is always on the proposer's side.
 *
 * The status gate matters because an open amendment survives
 * `active → closing/canceled` untouched, while the api locks the amendable
 * engagement first and rejects every action on a non-active one.
 */
export function canConfirmAmendmentFor(
  engagementStatus: string,
  amendment: { status: string; proposedByUserId: string },
  role: ViewerRole,
  expertId: string | null
): boolean {
  if (engagementStatus !== 'active' || amendment.status !== 'proposed') return false;
  const viewerSide = amendmentSideOf(role);
  if (viewerSide === null) return false;
  // Propose is party-gated too, so a proposer who is not the expert is
  // necessarily a work approver — i.e. customer side.
  const proposerSide: AmendmentSide =
    expertId !== null && amendment.proposedByUserId === expertId ? 'expert' : 'customer';
  return viewerSide !== proposerSide;
}

/**
 * Withdraw: the proposer, while their proposal is still live — and, like
 * confirm, only while the engagement is `active` (the api's
 * `lockAmendableEngagement` gate). Available on a `pending_governance` row
 * too — parking it for approval does not take it out of the proposer's hands.
 */
export function canWithdrawAmendmentFor(
  engagementStatus: string,
  amendment: { status: string; proposedByUserId: string },
  viewerUserId: string
): boolean {
  if (engagementStatus !== 'active') return false;
  if (amendment.status !== 'proposed' && amendment.status !== 'pending_governance') return false;
  return amendment.proposedByUserId === viewerUserId;
}
